import { useInfiniteQuery } from '@tanstack/react-query'
import useFilters from 'ext-ui/hooks/filters/filters'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { matchPath } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import BoxAndLinks from 'src/components/BoxAndLinks/BoxAndLinks'
import { NavAwayProps } from 'src/components/NavAwayBox/NavAwayBox'
import { TableEmptyStateDesigns } from 'src/components/TableEmptyState/TableEmptyState'
import VirtualCardsBox from 'src/components/VirtualCardsBox/VirtualCardsBox'
import { useVirtualCardsBulkActions } from 'src/components/VirtualCardsBoxAndLinks/useVirtualCardsBulkActions'
import {
  makeBaseVirtualCardsSchema,
  VirtualCardsAnalyticsEventProperties,
  VirtualCardsFilters,
  VirtualCardsFiltersBody,
  VirtualCardsListContext,
} from 'src/components/VirtualCardsBoxAndLinks/virtualCardsFilter.schema'
import { BulkAction } from 'src/hooks/bulkActionHooks'
import { VirtualCardListModel } from 'src/models/virtualCard.models'
import { accountsUrl, budgetsUrl } from 'src/navigation/urls'
import { virtualCardQueries } from 'src/queries/virtualCards.queries'
import { GET_VIRTUAL_CARDS_SUCCESS } from 'src/redux/actions/data/virtualCards.actions'
import {
  selectCalculatedBackgroundLocation,
  selectIsLeftFlyoutOpen,
  selectIsRightFlyoutOpen,
} from 'src/redux/selectors/router.selectors'
import { InfiniteScrollProps } from 'src/types'

export type {
  VirtualCardsAnalyticsEventProperties,
  VirtualCardsFilters,
  VirtualCardsFiltersBody,
} from 'src/components/VirtualCardsBoxAndLinks/virtualCardsFilter.schema'
export { VirtualCardsListContext } from 'src/components/VirtualCardsBoxAndLinks/virtualCardsFilter.schema'

type VirtualCardsBoxAndLinksProps = {
  onDownloadClick?: (filters: VirtualCardsFiltersBody) => void
  defaultTitle?: React.ReactElement
  virtualCards?: VirtualCardListModel[]
  navAway?: NavAwayProps
  emptyStateDesign: TableEmptyStateDesigns
  isYourCards?: boolean
  hideFilters?: boolean
  infiniteScrollProps?: InfiniteScrollProps<VirtualCardsFiltersBody>
  initialFilterValues?: VirtualCardsInitialFilterValues
  usersFullPaginatedData?: unknown
  totalCount: number
  readOnly?: boolean
  setSelectedFilters?: (filters: VirtualCardsAnalyticsEventProperties) => void
  downloadPending?: boolean
  clientHeight?: number
  getVirtualCardDetailUrl?: (virtualCardId: string) => string
  context?: VirtualCardsListContext
  userId?: string
  creditCardId?: string
  showBulkActions?: boolean
  selectedVirtualCardIds?: string[]
  setSelectedVirtualCardIds?: (ids: string[]) => void
  renderBulkActionsToolbar?: (args: {
    selectedVirtualCards: VirtualCardListModel[]
    clearSelection: () => void
    handleBulkAction: (bulkAction?: BulkAction) => void
  }) => React.ReactNode
  onBulkSelectionLimit?: () => void
  onBulkSelectionAnalytics?: (totalSelectedCards: number) => void
  onBulkAction?: (selectedIds: string[]) => void
  onBulkActionAnalytics?: (
    bulkAction: BulkAction | undefined,
    selectedVirtualCards: VirtualCardListModel[],
  ) => void
}

type VirtualCardsInitialFilterValues = Omit<
  Partial<VirtualCardsFilters>,
  'recipients' | 'statuses' | 'cardTypes'
> & {
  keyword?: string
  recipients?: string[] | Set<string>
  statuses?: string[] | Set<string>
  cardTypes?: string[] | Set<string>
}

const toStringSet = (
  value?: string[] | Set<string>,
): Set<string> | undefined => (Array.isArray(value) ? new Set(value) : value)

const normalizeInitialFilters = (
  filters?: VirtualCardsInitialFilterValues,
): Partial<VirtualCardsFilters> | undefined => {
  if (!filters) {
    return undefined
  }

  return {
    search: filters.search ?? filters.keyword,
    recipients: toStringSet(filters.recipients),
    statuses: toStringSet(filters.statuses),
    cardTypes: toStringSet(filters.cardTypes),
    deactivatingIn: filters.deactivatingIn,
    sortBy: filters.sortBy,
    sortDescending: filters.sortDescending,
  }
}

const VirtualCardsBoxAndLinks: React.FC<VirtualCardsBoxAndLinksProps> = (
  props,
): React.ReactElement => {
  const { analytics } = useAnalyticsContext()
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const isRightFlyoutOpen = useSelector(selectIsRightFlyoutOpen)
  const isLeftFlyoutOpen = useSelector(selectIsLeftFlyoutOpen)
  const context = props.context ?? VirtualCardsListContext.AllCards
  const initialFilters = useMemo(
    () => normalizeInitialFilters(props.initialFilterValues),
    [props.initialFilterValues],
  )

  const handlePageLocation = (): string => {
    return matchPath(calculatedBackgroundLocation.pathname, accountsUrl())
      ? 'accounts'
      : matchPath(calculatedBackgroundLocation.pathname, budgetsUrl())
        ? 'budgets'
        : ''
  }

  const {
    filters,
    apiQueryParams,
    handleFilterChange,
    resetFilters,
    resetSingleFilter,
    isDirty,
    selectedFiltersCount,
  } = useFilters<VirtualCardsFilters, VirtualCardsFiltersBody>(
    useMemo(
      () =>
        makeBaseVirtualCardsSchema({
          context,
          userId: props.userId,
          creditCardId: props.creditCardId,
          initialFilters,
        }),
      [context, props.userId, props.creditCardId, initialFilters],
    ),
  )

  const filtersBody = apiQueryParams

  const queryEnabled =
    context === VirtualCardsListContext.MyWallet
      ? props.userId !== undefined
      : [
            VirtualCardsListContext.Account,
            VirtualCardsListContext.Budget,
          ].includes(context)
        ? props.creditCardId !== undefined
        : true

  const dispatch = useDispatch()
  const listQueryOptions = virtualCardQueries.list(filtersBody)
  const fetchListPage = listQueryOptions.queryFn
  const { data, fetchNextPage, isFetching, hasNextPage } = useInfiniteQuery({
    ...listQueryOptions,
    // Mirror each fetched page into Redux: the bulk overlays mounted in
    // Authed.tsx (BulkUpdateVirtualCardFlyout, BulkDeactivate/BulkClose popups)
    // and VirtualCardForm resolve the selected card models from
    // state.data.virtualCards.listResponses, so the list fetch must keep
    // feeding it or bulk actions crash on a fresh session.
    queryFn: async (context) => {
      if (typeof fetchListPage !== 'function') {
        throw new Error('virtualCardQueries.list did not provide a queryFn')
      }
      const page = await fetchListPage(context)
      dispatch({ type: GET_VIRTUAL_CARDS_SUCCESS, payload: page })
      return page
    },
    enabled: queryEnabled && filtersBody !== undefined,
  })
  // `complete` gates the "no virtual cards" empty state. Require a resolved
  // query (`data !== undefined`) so a disabled/not-yet-fetched query (e.g.
  // Account/Budget waiting on creditCardId) does not flash the empty state
  // before the first fetch.
  const complete = data !== undefined && !hasNextPage && !isFetching

  const virtualCards = data?.virtualCards ?? []
  const totalItems = data?.totalItems ?? 0

  const {
    checkboxProps,
    selectedVirtualCards,
    handleBulkAction,
    clearSelection,
  } = useVirtualCardsBulkActions({
    enabled: props.showBulkActions === true,
    visibleVirtualCards: virtualCards,
    selectedIds: props.selectedVirtualCardIds ?? [],
    setSelectedIds: props.setSelectedVirtualCardIds ?? (() => undefined),
    onSelectionLimit: props.onBulkSelectionLimit ?? (() => undefined),
    onSelectionAnalytics: props.onBulkSelectionAnalytics ?? (() => undefined),
    onBulkAction: props.onBulkAction ?? (() => undefined),
    onBulkActionAnalytics: props.onBulkActionAnalytics ?? (() => undefined),
  })

  const infiniteScrollProps: InfiniteScrollProps<VirtualCardsFiltersBody> =
    useMemo(
      () => ({
        fetch: () => {
          if (hasNextPage && !isFetching) {
            fetchNextPage()
          }
        },
        page: data?.page,
        loading: isFetching,
        complete,
        totalItems,
        hasNextPage,
      }),
      [
        complete,
        data?.page,
        fetchNextPage,
        hasNextPage,
        isFetching,
        totalItems,
      ],
    )

  // Read the latest filters/handler at flyout-close time without adding them to
  // the effect deps — depending on them would re-restore on every filter edit.
  const handleFilterChangeRef = useRef(handleFilterChange)
  handleFilterChangeRef.current = handleFilterChange
  const filtersRef = useRef(filters)
  filtersRef.current = filters

  // restores query params on flyout close
  useEffect((): void => {
    if (!isRightFlyoutOpen && !isLeftFlyoutOpen) {
      handleFilterChangeRef.current(filtersRef.current)
    }
  }, [isRightFlyoutOpen, isLeftFlyoutOpen])

  useEffect((): void => {
    props.setSelectedFilters !== undefined &&
      props.setSelectedFilters({
        card_user:
          filters.recipients && filters.recipients.size > 0
            ? Array.from(filters.recipients)
            : undefined,
        status:
          filters.statuses && filters.statuses.size > 0
            ? Array.from(filters.statuses)
            : undefined,
        search: filters.search,
        card_type:
          filters.cardTypes && filters.cardTypes.size > 0
            ? Array.from(filters.cardTypes)
            : undefined,
        deactivating_in: filters.deactivatingIn,
      })
  }, [
    filters.search,
    filters.statuses,
    filters.recipients,
    filters.cardTypes,
    filters.deactivatingIn,
    filters.sortBy,
    filters.sortDescending,
  ])

  useEffect((): void => {
    if (filters.search) {
      analytics?.track(
        handlePageLocation() + ' virtual cards filter - search cards - typing',
        {
          intent: 'filter cards',
          screen: calculatedBackgroundLocation.pathname,
          interaction: 'typing',
          results: totalItems,
          search: filters.search,
        },
      )
    }
  }, [totalItems, filters.search])

  const setSetFilter = useCallback(
    (key: 'statuses' | 'recipients' | 'cardTypes', value: Set<string>) => {
      handleFilterChange({ [key]: value })
    },
    [handleFilterChange],
  )

  const toggleSetFilter = useCallback(
    (key: 'statuses' | 'recipients' | 'cardTypes', value: string) => {
      const next = new Set(filters[key] ?? [])
      if (next.has(value)) {
        next.delete(value)
      } else {
        next.add(value)
      }
      handleFilterChange({ [key]: next })
    },
    [filters, handleFilterChange],
  )

  const setSort = useCallback(
    (field: string): void => {
      if (filters.sortBy === field) {
        if (filters.sortDescending === false) {
          handleFilterChange({ sortDescending: true })
        } else {
          handleFilterChange({ sortBy: undefined, sortDescending: undefined })
        }
      } else {
        handleFilterChange({ sortBy: field, sortDescending: false })
      }
    },
    [filters.sortBy, filters.sortDescending, handleFilterChange],
  )

  return (
    <BoxAndLinks>
      <VirtualCardsBox
        {...props}
        virtualCards={virtualCards}
        loading={isFetching}
        complete={complete}
        readOnly={!!props.readOnly}
        isDirty={isDirty}
        selectedFiltersCount={selectedFiltersCount}
        resetFilters={resetFilters}
        resetSingleFilter={resetSingleFilter}
        fullProps={{
          keywordFilter: filters.search ?? '',
          setKeywordFilter: (value: string) =>
            handleFilterChange({ search: value }),
          statusesFilter: Array.from(filters.statuses ?? []),
          setStatusesFilter: (value: Set<string>) =>
            setSetFilter('statuses', value),
          handleStatusSelection: (value: string) =>
            toggleSetFilter('statuses', value),
          recipientFilter: Array.from(filters.recipients ?? []),
          setRecipientFilter: (value: Set<string>) =>
            setSetFilter('recipients', value),
          handleRecipientSelection: (value: string) =>
            toggleSetFilter('recipients', value),
          cardTypesFilter: Array.from(filters.cardTypes ?? []),
          setCardTypesFilter: (value: Set<string>) =>
            setSetFilter('cardTypes', value),
          handleCardTypesSelection: (value: string) =>
            toggleSetFilter('cardTypes', value),
          deactivatingInFilter: filters.deactivatingIn ?? '',
          setDeactivatingInFilter: (value: string) =>
            handleFilterChange({ deactivatingIn: value }),
          sortableProps: {
            setSort,
            sortBy: filters.sortBy,
            sortDescending: filters.sortDescending,
          },
          filteredCount: totalItems,
        }}
        isYourCards={props.isYourCards}
        hideFilters={props.hideFilters}
        emptyStateDesign={props.emptyStateDesign}
        checkboxProps={checkboxProps}
        onDownloadClick={props.onDownloadClick}
        downloadPending={props.downloadPending}
        infiniteScrollProps={infiniteScrollProps}
        filtersBody={filtersBody}
        clientHeight={props.clientHeight}
        getVirtualCardDetailUrl={props.getVirtualCardDetailUrl}
      />
      {props.renderBulkActionsToolbar?.({
        selectedVirtualCards,
        clearSelection,
        handleBulkAction,
      })}
    </BoxAndLinks>
  )
}

export default VirtualCardsBoxAndLinks
